import type { SimChallengeResults, SimShareResponse, SimTramoScore } from '@cruz/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { simApi, simErrorMessage } from './api.ts';
import { Board, Intro, mistakes, seconds } from './TramoBoard.tsx';
import { card, Notice, PageTitle, Working } from './ui.tsx';

/*
 * A tramo challenge, at /simulador/logro/:id: a friend plays the same tramo the player
 * put in order, once and without an account, and then sees who did it better. Whoever
 * shared it sees how many friends have tried and how many beat them; never a ranking.
 */

type Phase = 'intro' | 'play' | 'sending';

const time = (t: SimTramoScore) => (t.complete ? `${seconds(t.ms)} s` : 'Sin terminar');
const stars = (n: number) => `${'★'.repeat(n)}${'☆'.repeat(3 - n)}`;
const friends = (n: number) => (n === 1 ? '1 amigo' : `${n} amigos`);

export function ChallengePage({
  share,
  voter,
  cta,
  report,
}: {
  share: SimShareResponse;
  /** This browser's id: one try per challenge */
  voter: () => string;
  /** To start a career of one's own */
  cta: ReactNode;
  report: ReactNode;
}) {
  const c = share.card;
  const owner = c.tramo!;
  const puzzle = share.challenge?.puzzle ?? null;
  const [results, setResults] = useState<SimChallengeResults | null>(
    share.challenge?.results ?? null,
  );
  const [phase, setPhase] = useState<Phase>('intro');
  const [error, setError] = useState<string | null>(null);

  const finish = async (taps: string[], ms: number) => {
    setPhase('sending');
    try {
      setResults(await simApi.playChallenge(c.id, taps, ms, voter()));
      scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(simErrorMessage(err));
      setPhase('intro');
    }
  };

  if (results && !results.mine) return <OwnChallenge share={share} results={results} />;
  if (results) {
    return (
      <div className="space-y-5">
        <Verdict nickname={c.nickname} results={results} />
        <div className="space-y-3 border-t border-gold/40 pt-5">
          <p className="text-lg">
            En <em>De cirio a hermano mayor</em> fundas tu hermandad y cada día te toca un tramo
            nuevo. Empiezas de cirio, en el primer tramo.
          </p>
          {cta}
        </div>
        {report}
      </div>
    );
  }
  if (!puzzle) return <Notice tone="error">Este reto ya no se puede jugar.</Notice>;
  if (phase === 'sending') return <Working label="Contando los nazarenos…" />;
  if (phase === 'play') return <Board puzzle={puzzle} onFinish={(t, ms) => void finish(t, ms)} />;
  return (
    <div className="space-y-4">
      <Intro
        puzzle={puzzle}
        kicker={`Reto de ${c.nickname} · Nivel ${owner.level}`}
        note={`${c.nickname}: ${time(owner)}, ${mistakes(owner.errors)}. Tienes ${puzzle.seconds} segundos y un solo intento.`}
        onStart={() => {
          setPhase('play');
          scrollTo({ top: 0 });
        }}
      />
      {error && <Notice tone="error">{error}</Notice>}
      <p className="text-ink-soft dark:text-cream/75">
        {c.nickname} juega a <em>De cirio a hermano mayor</em> con su hermandad, {c.brotherhood}.
        Para jugar el reto no hace falta registrarse.
      </p>
      {report}
    </div>
  );
}

const VERDICT = {
  win: (n: string) => `¡Le has ganado a ${n}!`,
  draw: (n: string) => `Empate con ${n}`,
  lose: (n: string) => `${n} sigue por delante`,
};

function Verdict({ nickname, results }: { nickname: string; results: SimChallengeResults }) {
  const mine = results.mine!;
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), []);
  const others = results.tries - 1;
  const beatenBefore = results.beaten - (results.outcome === 'win' ? 1 : 0);
  return (
    <article aria-labelledby="challenge-result" className="space-y-4">
      <div className="animate-crown rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700">
        <p className="text-xs font-semibold tracking-[0.16em] text-gold-300 uppercase">
          Reto de {nickname}
        </p>
        <h1
          id="challenge-result"
          ref={titleRef}
          tabIndex={-1}
          className="mt-2 font-serif text-[2rem] leading-tight font-semibold outline-none"
        >
          {VERDICT[results.outcome ?? 'lose'](nickname)}
        </h1>
        <p className="mt-2 text-cream/85">
          {results.outcome === 'lose' && !mine.complete
            ? 'Se acabó el tiempo antes de formar el tramo.'
            : 'Primero cuenta formar el tramo entero; después, los fallos; y al final, el tiempo.'}
        </p>
      </div>
      <Scores
        rows={[
          { who: 'Tú', score: mine },
          { who: nickname, score: results.owner },
        ]}
      />
      {others > 0 && (
        <p className="text-ink-soft dark:text-cream/75">
          Antes que tú {others === 1 ? 'lo ha' : 'lo han'} intentado {friends(others)} de {nickname}
          .{' '}
          {beatenBefore === 0
            ? 'Ninguno le ganó.'
            : beatenBefore === 1
              ? 'Uno le ganó.'
              : `${beatenBefore} le ganaron.`}
        </p>
      )}
    </article>
  );
}

function Scores({ rows }: { rows: { who: string; score: SimTramoScore }[] }) {
  return (
    <table className={`${card} w-full overflow-hidden text-left`}>
      <caption className="sr-only">Resultados</caption>
      <thead>
        <tr className="text-sm text-ink-soft dark:text-cream/75">
          <th scope="col" className="px-4 pt-3 pb-1 font-semibold">
            <span className="sr-only">Quién</span>
          </th>
          <th scope="col" className="px-2 pt-3 pb-1 font-semibold">
            Tiempo
          </th>
          <th scope="col" className="px-4 pt-3 pb-1 text-right font-semibold">
            Fallos
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ who, score }) => (
          <tr key={who} className="border-t border-purple/10 dark:border-cream/10">
            <th scope="row" className="px-4 py-3 font-semibold">
              {who}
              <span
                className="block text-gold-700 dark:text-gold-300"
                aria-label={`${score.stars} de 3 estrellas`}
                role="img"
              >
                {stars(score.stars)}
              </span>
            </th>
            <td className="px-2 py-3 font-serif text-xl font-semibold tabular">{time(score)}</td>
            <td className="px-4 py-3 text-right font-serif text-xl font-semibold tabular">
              {score.errors}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Whoever shared it: their time and what friends did, without names or a ranking. */
function OwnChallenge({
  share,
  results,
}: {
  share: SimShareResponse;
  results: SimChallengeResults;
}) {
  return (
    <div className="space-y-5">
      <PageTitle kicker="Tu reto">Ordena el tramo</PageTitle>
      <Scores rows={[{ who: 'Tú', score: results.owner }]} />
      <p className="text-lg">
        {results.tries === 0
          ? 'Todavía no lo ha intentado nadie. Mándaselo a quien creas que puede ganarte.'
          : `Lo ${results.tries === 1 ? 'ha' : 'han'} intentado ${friends(results.tries)}. ${
              results.beaten === 0
                ? 'Ninguno te ha ganado.'
                : results.beaten === 1
                  ? 'Uno te ha ganado.'
                  : `${results.beaten} te han ganado.`
            }`}
      </p>
      <img
        src={share.image_story}
        alt={`Tarjeta del reto: ${share.card.phrase}`}
        width={1080}
        height={1920}
        className="mx-auto h-auto max-h-[50dvh] w-auto rounded-2xl border border-gold/60 shadow-lg"
      />
    </div>
  );
}
